import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Divider,
  Button,
  TextField,
  CircularProgress,
  Avatar,
  useTheme,
  IconButton,
  Grid,
  Card,
} from "@mui/material";
import CowIcon from "../CowIcon";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useAppSelector } from "../../hooks/hooks";

interface InvestmentPoolDetailViewProps {
  //   pool: LoanPool;
  onBack: () => void;
  onInvest: (amount: number, poolId: string) => Promise<void>;
  isMobile?: boolean;
}

const InvestmentPoolDetailView: React.FC<InvestmentPoolDetailViewProps> = ({
  onBack,
  onInvest,
  isMobile = false,
}) => {
  const theme = useTheme();
  const [investmentAmount, setInvestmentAmount] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const pool = useAppSelector((state) => state.ui.selectedLoanPool);

  const scrollableBoxStyles = {
    maxHeight: isMobile ? "76vh" : "68vh",
    overflowY: "auto",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setInvestmentAmount(value);
      setError(null);
    }
  };

  const handleInvest = async () => {
    if (!investmentAmount || parseFloat(investmentAmount) <= 0) {
      setError("Please enter a valid investment amount");
      return;
    }

    setIsSubmitting(true);
    try {
      if (!pool?.id) {
        setError("Pool information is missing. Cannot invest.");
        setIsSubmitting(false);
        return;
      }
      await onInvest(parseFloat(investmentAmount), pool.id);
      // Optionally reset form or navigate away after successful investment
    } catch (err) {
      setError("Investment failed. Please try again.");
      console.error("Investment error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Paper
      sx={{
        p: isMobile ? 2 : 3,
        borderRadius: 2,
        maxWidth: 600,
        margin: "0 auto",
        ...scrollableBoxStyles,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton onClick={onBack} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h2">
          Invest in {pool?.name}
        </Typography>
      </Box>

      <Stack spacing={3}>
        {/* Pool Summary */}
        <Card variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Avatar sx={{ bgcolor: "primary.main", width: 56, height: 56 }}>
              <CowIcon size={32} fill="#fff" />
            </Avatar>
            <Box>
              <Typography variant="h6">{pool?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                Cattle-backed loan pool
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid>
              <Typography variant="body2" color="text.secondary">
                Total Value
              </Typography>
              <Typography variant="h6">{pool?.totalValue} NDEIPI</Typography>
            </Grid>
            <Grid>
              <Typography variant="body2" color="text.secondary">
                Active Loans
              </Typography>
              <Typography variant="h6">{pool?.activeLoans}</Typography>
            </Grid>
            <Grid>
              <Typography variant="body2" color="text.secondary">
                Avg. Interest
              </Typography>
              <Typography variant="h6">{pool?.avgInterest}%</Typography>
            </Grid>
            <Grid>
              <Typography variant="body2" color="text.secondary">
                Default Rate
              </Typography>
              <Typography variant="h6">{pool?.defaultRate}%</Typography>
            </Grid>
          </Grid>
        </Card>

        {/* Investment Form */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Investment Details
          </Typography>

          <TextField
            fullWidth
            label="Amount to Invest (NDEIPI)"
            variant="outlined"
            value={investmentAmount}
            onChange={handleAmountChange}
            type="text" // Using text to handle decimal points properly
            inputMode="decimal"
            error={!!error}
            helperText={error}
            sx={{ mb: 3 }}
          />

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Estimated Returns
            </Typography>
            {investmentAmount && parseFloat(investmentAmount) > 0 ? (
              <Stack spacing={1}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography>Annual Interest:</Typography>
                  <Typography fontWeight="medium">
                    {(
                      (parseFloat(investmentAmount) *
                        (pool?.avgInterest ?? 0)) /
                      100
                    ).toFixed(2)}{" "}
                    NDEIPI
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography>Monthly:</Typography>
                  <Typography fontWeight="medium">
                    {(
                      (parseFloat(investmentAmount) *
                        (pool?.avgInterest ?? 0)) /
                      100 /
                      12
                    ).toFixed(2)}{" "}
                    NDEIPI
                  </Typography>
                </Box>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Enter an amount to see estimated returns
              </Typography>
            )}
          </Box>

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleInvest}
            disabled={
              isSubmitting ||
              !investmentAmount ||
              parseFloat(investmentAmount) <= 0
            }
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                Processing...
              </>
            ) : (
              "Confirm Investment"
            )}
          </Button>
        </Box>

        {/* Risk Disclaimer */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Note:</strong> Investments in cattle loan pools carry risks
            including potential loss of principal. Past performance is not
            indicative of future results. Please review the pool details
            carefully before investing.
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
};

export default InvestmentPoolDetailView;
